import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { publicApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { Trophy, Calendar, ArrowRight, Users, Newspaper } from "lucide-react";

/**
 * MODERN — Clean card-based layout, neutral palette emphasis.
 * Sections clearly separated, calm typography, ample whitespace.
 */
export default function ModernTemplate() {
    const { branding } = useBranding();
    const [data, setData] = useState({ slides: [], next: null, last: null, players: [], posts: [], standings: [], sponsors: [] });

    useEffect(() => {
        Promise.all([
            publicApi.heroSlides().catch(() => []),
            publicApi.nextMatch().catch(() => null),
            publicApi.lastMatch().catch(() => null),
            publicApi.players().catch(() => []),
            publicApi.posts({ limit: 6 }).catch(() => []),
            publicApi.standings().catch(() => []),
            publicApi.sponsors().catch(() => []),
        ]).then(([slides, next, last, players, posts, standings, sponsors]) =>
            setData({ slides, next, last, players, posts, standings, sponsors })
        );
    }, []);

    const primary = branding.primary_color || "#f5dc4c";

    return (
        <PublicLayout>
            <div data-testid="template-modern" className="bg-[#fafafa] text-[#0a0a0a]">
                {/* Clean Hero */}
                <section className="relative pt-24 pb-20">
                    <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: primary }}>Modern Kulüp</div>
                            <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6">{branding.name}</h1>
                            <p className="text-lg text-neutral-600 mb-8">{branding.description || "Kulübümüz, geleceğin futbolcularını yetiştiriyor ve taraftarına en iyiyi sunuyor."}</p>
                            <div className="flex gap-3">
                                <Link to="/oyuncular" className="px-6 py-3 font-bold text-sm rounded-full transition-all" style={{ background: primary, color: "#000" }}>Kadromuz <ArrowRight size={14} className="inline ml-1"/></Link>
                                <Link to="/mac-merkezi" className="px-6 py-3 font-bold text-sm rounded-full transition-all border-2 border-[#0a0a0a]">Maç Merkezi</Link>
                            </div>
                        </div>
                        <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-200">
                            {data.slides[0]?.image_url && <img src={data.slides[0].image_url} alt="" className="w-full h-full object-cover" />}
                        </div>
                    </div>
                </section>

                {/* Cards: Stats / Next Match / Last Result */}
                <section className="container-x grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                    <Card icon={Calendar} title="Sonraki Maç" subtitle={data.next ? `${data.next.home_team} vs ${data.next.away_team}` : "Henüz planlanmadı"} detail={data.next?.match_date?.slice(0, 10)} primary={primary} />
                    <Card icon={Trophy} title="Son Sonuç" subtitle={data.last ? `${data.last.home_team} ${data.last.home_score}-${data.last.away_score} ${data.last.away_team}` : "—"} detail={data.last?.match_date?.slice(0, 10)} primary={primary} />
                    <Card icon={Users} title="Kadro" subtitle={`${data.players.length} oyuncu`} detail="Aktif sezon" primary={primary} />
                </section>

                {/* Posts grid */}
                {data.posts.length > 0 && (
                    <section className="container-x mb-20">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-3xl font-extrabold tracking-tight flex items-center gap-3"><Newspaper size={24}/> Haberler</h2>
                            <Link to="/haberler" className="text-sm font-bold uppercase tracking-widest hover:underline" style={{ color: primary }}>Tümü →</Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {data.posts.slice(0, 6).map((p) => (
                                <Link key={p.id} to={`/haberler/${p.slug}`} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-neutral-200">
                                    <div className="aspect-[16/10] bg-neutral-100">
                                        {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover"/>}
                                    </div>
                                    <div className="p-5">
                                        <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: primary }}>{p.category}</div>
                                        <h3 className="font-bold mb-2">{p.title}</h3>
                                        <div className="text-xs text-neutral-500">{(p.published_at || "").slice(0, 10)}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Sponsors strip */}
                {data.sponsors.length > 0 && (
                    <section className="bg-white border-t border-neutral-200 py-12">
                        <div className="container-x">
                            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 text-center mb-6">Sponsorlarımız</div>
                            <div className="flex flex-wrap items-center justify-center gap-8 opacity-70 hover:opacity-100 transition-opacity">
                                {data.sponsors.slice(0, 10).map((s) => (
                                    <div key={s.id} className="h-12 flex items-center">
                                        {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-full object-contain" /> : <span className="text-sm font-bold">{s.name}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </PublicLayout>
    );
}

function Card({ icon: Icon, title, subtitle, detail, primary }) {
    return (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-200">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ background: `${primary}20`, color: primary }}>
                <Icon size={22}/>
            </div>
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">{title}</div>
            <div className="text-lg font-bold">{subtitle}</div>
            {detail && <div className="text-sm text-neutral-500 mt-1">{detail}</div>}
        </div>
    );
}
